import type {ReactElement} from 'react';
import React from 'react';
import type {DropdownItemStorage} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {ContextualMenuDropdownOption, ContextualMenuDropdownType} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {BaseFancyTextCurveOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-fancy-text-curve-option';
import {MorphType} from '@Libraries/fancy-text-library';

interface BaseFancyTextShapeOptionProps {
  id: string;
  textShape: MorphType;
  morphAmount: number;
  dropdownType?: ContextualMenuDropdownType;
  updateShape(newShape: MorphType): void;
  updateMorphAmount(amount: number): void;
  updateMorphAmountEnded(amount: number): void;
  isLoading?: boolean;
  isSubOptionDisabled?: boolean;
  selectorClasses?: string;
  isBeingOpenedFromModal?: boolean;
  showModalForSmallScreen?: boolean;
  modalTitle?: string;
}

export function BaseFancyTextShapeOption({
  dropdownType = ContextualMenuDropdownType.GHOST,
  isLoading = false,
  isSubOptionDisabled = false,
  selectorClasses = '',
  isBeingOpenedFromModal = false,
  showModalForSmallScreen = false,
  modalTitle = '',
  ...props
}: BaseFancyTextShapeOptionProps): ReactElement<any> {
  const showSubOption = (): boolean => {
    return props.textShape === MorphType.CURVE_UP || props.textShape === MorphType.CURVE_DOWN || props.textShape === MorphType.WAVE;
  };

  const onClickHandler = (clickedId: MorphType): void => {
    if (clickedId === props.textShape) {
      return;
    }
    props.updateShape(Number(clickedId));
  };

  const onChangeHandler = (amount: number): void => {
    props.updateMorphAmount(amount);
  };

  const onChangeEndedHandler = (amount: number): void => {
    props.updateMorphAmountEnded(amount);
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (!showSubOption()) {
      return null;
    }

    if (props.textShape === MorphType.CURVE_UP || props.textShape === MorphType.CURVE_DOWN || props.textShape === MorphType.WAVE) {
      return (
        <BaseFancyTextCurveOption
          morphAmount={props.morphAmount}
          updateMorphAmount={onChangeHandler}
          updateMorphAmountEnded={onChangeEndedHandler}
          isSubOptionDisabled={isSubOptionDisabled}
          morphType={props.textShape}
        />
      );
    }

    return null;
  };

  const getDropdownItems = (): Array<DropdownItemStorage> => {
    return [
      {
        id: MorphType.NONE,
        text: window.i18next.t('pmwjs_straight'),
      },
      {
        id: MorphType.CURVE_UP,
        text: window.i18next.t('pmwjs_curve_up'),
      },
      {
        id: MorphType.CURVE_DOWN,
        text: window.i18next.t('pmwjs_curve_down'),
      },
      {
        id: MorphType.WAVE,
        text: window.i18next.t('pmwjs_wave'),
      },
      {
        id: MorphType.WEDGE_LEFT,
        text: window.i18next.t('pmwjs_wedge_left'),
      },
      {
        id: MorphType.WEDGE_RIGHT,
        text: window.i18next.t('pmwjs_wedge_right'),
      },
      {
        id: MorphType.ROOF,
        text: window.i18next.t('pmwjs_roof'),
      },
    ];
  };

  if (isBeingOpenedFromModal) {
    return (
      <ContextualMenuDropdownOption
        dropdownItems={getDropdownItems()}
        position={DROPDOWN_POSITION.FIXED}
        id={props.id}
        label={window.i18next.t('pmwjs_text_shape')}
        onClick={onClickHandler}
        selectorType={dropdownType}
        selectedItemId={props.textShape}
        isLoading={isLoading}
        isDisabled={isLoading}
        selectorClasses={selectorClasses}
        popupMaxHeight={showModalForSmallScreen ? undefined : 250}
        popUpHasCustomWidth={false}
        doesSubOptionHaveTransition={!isBeingOpenedFromModal}
        showModalForSmallScreen={showModalForSmallScreen}
        modalTitle={modalTitle}
      >
        {getSubOptions()}
      </ContextualMenuDropdownOption>
    );
  }

  return (
    <PosterEditingBaseDropdownOption
      dropdownItems={getDropdownItems()}
      position={DROPDOWN_POSITION.FIXED}
      id={props.id}
      label={window.i18next.t('pmwjs_text_shape')}
      onClick={onClickHandler}
      selectorType={dropdownType}
      selectedItemId={props.textShape}
      isLoading={isLoading}
      isDisabled={isLoading}
      selectorClasses={selectorClasses}
      doesSubOptionHaveTransition={!isBeingOpenedFromModal}
    >
      {getSubOptions()}
    </PosterEditingBaseDropdownOption>
  );
}
